import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { FormattedMessage, useIntl } from 'react-intl';
import SingleColumnTemplate from '../../../../../../../ui/layouts/SingleColumnTemplate';
import Button from '../../../../../../../ui/canon/Button';

const USER_GENERATE_CASH_FLOWS = gql`
  mutation userGenerateCashFlows($seller_t12: Float) {
    userGenerateCashFlows(seller_t12: $seller_t12) {
      id
    }
  }
`;

const SuccessionCfaPrice = ({ updateStep, step, updateCashFlows, currentUser }) => {
  const intl = useIntl();
  // Initialize with user's gross_production if available, otherwise default to 0
  const initialT12 = currentUser?.gross_production || 0;
  const [sellerT12, updatePurchasePrice] = useState(initialT12);
  
  // Apollo mutation hook
  const [userGenerateCashFlows, { loading: mutationLoading }] = useMutation(USER_GENERATE_CASH_FLOWS);
  
  const company = currentUser?.company;
  const companyName = company?.host?.split('.')[0] || '';

  const handleInputChange = (value) => {
    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    updatePurchasePrice(numericValue);
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  return (
    <SingleColumnTemplate>
      <div className="text-center">
        <h1 className="text-3xl font-bold text-[var(--succession-text-primary)] mb-8">
          <FormattedMessage id="calculators.cfa_wizard.specify_the_purchase_price" />
        </h1>

        {/* Input Section */}
        <div className="bg-[var(--succession-bg-tertiary)] rounded-lg shadow-succession-lg p-8 mb-8 max-w-md mx-auto border-2 border-[var(--succession-border)]">
          <div className="mb-6">
            <label className="block text-lg font-semibold text-[var(--succession-text-primary)] mb-4">
              <FormattedMessage id="calculators.cfa_wizard.seller_t12" />
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--succession-text-muted)] text-lg">$</span>
              <input
                type="text"
                value={sellerT12}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="0"
                style={{ backgroundColor: 'var(--succession-bg-primary)', color: 'var(--succession-text-primary)' }}
                className="w-full pl-8 pr-4 py-3 text-xl border-2 border-[var(--succession-border)] rounded-lg focus:border-[var(--succession-border-hover)] focus:ring-2 focus:ring-[var(--succession-focus-ring)] outline-none transition-all duration-200 text-right placeholder:text-[var(--succession-text-muted)]"
              />
            </div>
          </div>
        </div>

        {/* Continue Button with Mutation */}
        <Button
          variant="primary"
          size="lg"
          disabled={!sellerT12 || mutationLoading}
          onClick={() => {
            userGenerateCashFlows({
              variables: { 
                seller_t12: parseFloat(sellerT12) 
              }
            }).then(({ data }) => {
              updateCashFlows(data?.userGenerateCashFlows || []);
              updateStep(step + 1);
            }).catch(error => {
              console.error('Error generating cash flows:', error);
              alert('Error generating cash flows. Please try again.');
            });
          }}
          className="px-8 py-3 text-lg"
        >
          {mutationLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              {intl.formatMessage({ id: 'succession.cash_flow_analysis.price.generating' })}
            </div>
          ) : (
            <FormattedMessage id="calculators.cfa_wizard.continue" />
          )}
        </Button>

        {/* Help Text */}
        <div className="mt-8 text-[var(--succession-text-secondary)] max-w-lg mx-auto">
          <p>
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.price.help_text' })}
          </p>
        </div>
      </div>
    </SingleColumnTemplate>
  );
};

export default SuccessionCfaPrice; 